import fetch from "isomorphic-fetch";
import { get } from "lodash/fp";
import { fetch as router } from "helpers/router";

// ------ Variables ------

const resultsPerPage = 8;
const apiUrl = router("api.v2.campaigns.index");

// ------ Helpers ------

const buildFilterQuery = (filterName, filters) => {
  if (!Array.isArray(filters) || !filters.length) {
    return "";
  }
  return filters.reduce((string, currentFilter) => `${string}&${filterName}[]=${encodeURIComponent(currentFilter)}`, "");
};

const getFilterValues = (filters, propName = "value") => filters.map((f) => f[propName]);

// ------ Actions ------

const REQUEST_RESULTS = "browseResults/REQUEST_RESULTS";
const RECEIVE_RESULTS = "browseResults/RECEIVE_RESULTS";
const REQUEST_MORE_RESULTS = "browseResults/REQUEST_MORE_RESULTS";
const RECEIVE_MORE_RESULTS = "browseResults/RECEIVE_MORE_RESULTS";

// ------ Reducer ------

const initialState = {
  data: get("Chuffed.campaigns.data", window) || [],
  isFetching: false,
  isFetchingMore: false,
  nextCursor: get("Chuffed.campaigns.meta.cursor.next", window) || null,
  lastRequestUrl: null,
};

const rootReducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case REQUEST_RESULTS:
      return {
        ...state,
        isFetching: true,
        lastRequestUrl: action.url,
      };
    case RECEIVE_RESULTS:
      return {
        ...state,
        isFetching: false,
        data: action.data,
        nextCursor: action.meta.cursor.next,
      };
    case REQUEST_MORE_RESULTS:
      return {
        ...state,
        isFetchingMore: true,
      };
    case RECEIVE_MORE_RESULTS:
      return {
        ...state,
        isFetchingMore: false,
        data: [
          ...state.data,
          ...action.data,
        ],
        nextCursor: action.meta.cursor.next,
      };
    default: return state;
  }
};
export default rootReducer;

// ------ Synchronous Actions ------

export const requestResults = (url) => ({
  type: REQUEST_RESULTS,
  url,
});

export const receiveResults = ({ data, meta }) => ({
  type: RECEIVE_RESULTS,
  data,
  meta,
});

export const requestMoreResults = () => ({
  type: REQUEST_MORE_RESULTS,
});

export const receiveMoreResults = ({ data, meta }) => ({
  type: RECEIVE_MORE_RESULTS,
  data,
  meta,
});

// ------ Asynchronous Actions ------

export const fetchResults = () => (dispatch, getState) => {
  const {
    browseHero: {
      selectedCountries,
      selectedCategories,
      selectedOrderBy,
    },
    results: {
      lastRequestUrl,
    },
    advancedFilters: {
      selectedOwnerType,
      selectedPercentageRaised,
    },
  } = getState();

  const categoriesQuery = buildFilterQuery("categories", getFilterValues(selectedCategories));
  const countriesQuery = buildFilterQuery("countries", getFilterValues(selectedCountries));
  const ownerTypeQuery = buildFilterQuery("ownerType", getFilterValues(selectedOwnerType));
  const percentageRaisedQuery = buildFilterQuery("percentageRaised", getFilterValues(selectedPercentageRaised));
  const orderByQuery = selectedOrderBy ? `&orderBy=${getFilterValues(selectedOrderBy)}` : "";

  const requestUrl = `${apiUrl}?perPage=${resultsPerPage}${categoriesQuery}${countriesQuery}${ownerTypeQuery}${orderByQuery}${percentageRaisedQuery}`;

  // if no filters have been applied or the url hasn't changed then we don't need to request
  if (
    (
      !lastRequestUrl
      && !categoriesQuery
      && !countriesQuery
      && !orderByQuery
      && !ownerTypeQuery
      && !percentageRaisedQuery
    )
      || requestUrl === lastRequestUrl
  ) {
    return false;
  }

  dispatch(requestResults(requestUrl));

  return fetch(requestUrl, { credentials: "same-origin" })
    .then((response) => response.json())
    .then((json) => {
      dispatch(receiveResults(json));
    });
};

export const fetchMoreResults = () => (dispatch, getState) => {
  const {
    results: {
      isFetchingMore,
      nextCursor,
    },
  } = getState();

  if (isFetchingMore || !nextCursor) {
    return false;
  }

  const requestUrl = `${apiUrl}${nextCursor}`;

  dispatch(requestMoreResults());

  return fetch(requestUrl, { credentials: "same-origin" })
    .then((response) => response.json())
    .then((json) => {
      dispatch(receiveMoreResults(json));
    });
};
